import { FC, useEffect, useRef } from 'react';

import { Employer } from '@libs/models';
import { useForm } from '@carcareers/contexts';
import { AutocompleteSearch, SearchParams } from '@carcareers/components';
import { employerApi } from '@carcareers/store';

interface EmployerSelectProps {
  name: string;
  jobName?: string;
  clearJob?: boolean;
  defaultValue?: { label: string; value: Employer | number };
}

export const EmployerSelect: FC<EmployerSelectProps> = ({ name, jobName = 'job', clearJob = false, defaultValue }) => {
  const { setData, getData } = useForm<Employer>();
  const dataLoadedRef = useRef(false);
  const clearJobDoneRef = useRef(false);
  const defaultValueSetRef = useRef(false);
  
  const [searchEmployer, { isLoading }] = employerApi.endpoints.employerSearch.useLazyQuery();

  const wrappedSearch = async (params: SearchParams) => {
    try {
      const result = await searchEmployer(params);
      return result.data;
    } catch (error) {
      console.error('Search error:', error);
      return { items: [] };
    }
  };

  useEffect(() => {
    if (defaultValue && !dataLoadedRef.current) {
      if (typeof defaultValue.value === 'number') {
        searchEmployer({ q: defaultValue.value.toString(), page: 1, limit: 10 });
      } 
      else if (defaultValue.label && defaultValue.label.includes('@')) {
        const email = defaultValue.label.split(' - ')[1];
        if (email) {
          searchEmployer({ q: email, page: 1, limit: 10 });
        }
      }
      dataLoadedRef.current = true;
    }
  }, [defaultValue, searchEmployer]);

  useEffect(() => {
    if (defaultValue?.value && !defaultValueSetRef.current) {
      const currentValue = getData(name);
      if (!currentValue) {
        setData(name, defaultValue.value);
      }
      defaultValueSetRef.current = true;
    }
  }, [defaultValue, name, setData, getData]);

  useEffect(() => {
    if (clearJob && !clearJobDoneRef.current && dataLoadedRef.current) {
      setData(jobName, null);
      clearJobDoneRef.current = true;
    }
  }, [clearJob, jobName, setData, dataLoadedRef]);

  return (
    <AutocompleteSearch
      name={name}
      loading={isLoading}
      search={wrappedSearch}
      label="Employer"
      defaultValue={defaultValue}
    />
  );
};

export default EmployerSelect;
